import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  connect() {
    this.element.addEventListener("input", this.scheduleSearch);
    this.element.addEventListener("click", this.handleClick, true);

    const frameElement = this.frameElement;
    const { pending, caretStart, caretEnd } = this.focusAttributes;

    if (frameElement?.hasAttribute(pending)) {
      const start = frameElement.getAttribute(caretStart);
      const end = frameElement.getAttribute(caretEnd);

      this.clearPendingFocus();
      requestAnimationFrame(() => this.restoreFocus(start, end));
    }
  }

  disconnect() {
    clearTimeout(this.searchTimeout);

    this.element.removeEventListener("input", this.scheduleSearch);
    this.element.removeEventListener("click", this.handleClick, true);
  }

  get frameElement() {
    return this.element.closest("turbo-frame");
  }

  /** @returns {{ pending: string, caretStart: string, caretEnd: string }} */
  get focusAttributes() {
    throw new Error("Search controllers must provide focus attributes.");
  }

  /** @param {Event} event */
  handleClick = event => {
    const target = event.target;

    if (!(target instanceof Element)) return;

    if (target.closest('[data-cy="ds-base-input__clear-button"]')) {
      this.scheduleSearch();
      return;
    }

    if (target.closest("a")) {
      clearTimeout(this.searchTimeout);
      this.clearPendingFocus();
    }
  };

  scheduleSearch = () => {
    this.frameElement?.setAttribute(this.focusAttributes.pending, "");

    clearTimeout(this.searchTimeout);

    this.searchTimeout = setTimeout(() => {
      this.storeCaretPosition();

      const form = this.element.querySelector("form");

      if (form?.isConnected) {
        form.requestSubmit();
      }
    }, 500);
  };

  storeCaretPosition() {
    const frameElement = this.frameElement;

    /** @type {HTMLInputElement | null} */
    const input = this.element.querySelector("input[type='search']");

    if (!frameElement || !input) return;

    const { caretStart, caretEnd } = this.focusAttributes;

    frameElement.setAttribute(caretStart, String(input.selectionStart ?? input.value.length));
    frameElement.setAttribute(caretEnd, String(input.selectionEnd ?? input.value.length));
  }

  clearPendingFocus() {
    const frameElement = this.frameElement;

    if (!frameElement) return;

    const { pending, caretStart, caretEnd } = this.focusAttributes;

    frameElement.removeAttribute(pending);
    frameElement.removeAttribute(caretStart);
    frameElement.removeAttribute(caretEnd);
  }

  /** @param {string | null} caretStart @param {string | null} caretEnd */
  restoreFocus(caretStart, caretEnd) {
    /** @type {HTMLInputElement | null} */
    const input = this.element.querySelector("input[type='search']");

    if (!input) return;

    input.focus();

    const start = caretStart !== null ? Number(caretStart) : input.value.length;
    const end = caretEnd !== null ? Number(caretEnd) : input.value.length;

    input.setSelectionRange(start, end);
  }
}
